import { useCallback, useContext } from 'react';
import _ from 'underscore';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useInstrumentLogger } from '@bingads-webui-campaign-react/linkedin-instrumentation';
import { useWizardService, WIZARD_EVENTS, getWizardData } from '@bingads-webui-fluent-component/wizard';
import {
  readCampaign,
  createOrUpdateCampaign,
  readAdGroup,
  getDefaultCampaign,
  createOrUpdateAd,
  getDefaultAd,
  genericApiError,
  mapBulkUpsertErrors,
  getBulkUploadErrorType,
  ERROR_ENTITY,
  useLinkedInAdAdapter,
} from '@bingads-webui-campaign-react/linkedin-data';
import { Campaign, AdGroup, ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
  [getBulkUploadErrorType(ResponsiveAd)]: ERROR_ENTITY.Ad,
};

export const useLinkedInAdRepository = () => {
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext);
  const { cid, aid } = scope.values;
  const { state, send } = useWizardService();
  const {
    campaignId,
    adGroupId,
    campaignGoal,
    connectedCompanyPage,
    editingAdId,
    ads,
  } = getWizardData(state);

  const logger = useInstrumentLogger({
    scenarioName: 'LinkedInAdStep',
    activityName: 'Repository',
  });

  const { mapToBulkModel: mapLinkedInAdToBulkModel } = useLinkedInAdAdapter();

  const loadCampaign = useCallback(() => {
    if (!campaignId) {
      return Promise.resolve(getDefaultCampaign({ campaignGoal, i18n }));
    }
    return readCampaign(cid, aid, campaignId);
  }, [aid, campaignGoal, campaignId, cid, i18n]);

  const loadAdGroup = useCallback(() => {
    if (!campaignId || !adGroupId) {
      return Promise.resolve({});
    }
    return readAdGroup(cid, aid, campaignId, adGroupId);
  }, [adGroupId, aid, campaignId, cid]);

  const loadAd = useCallback(() => {
    if (!editingAdId) {
      // new ad
      return Promise.resolve(getDefaultAd({ connectedCompanyPage }));
    }

    return Promise.resolve(ads[editingAdId] || getDefaultAd({ connectedCompanyPage }));
  }, [editingAdId, ads, connectedCompanyPage]);

  const load = useCallback(() => {
    const initialRequests = [
      loadCampaign(),
      loadAdGroup(),
      loadAd(),
    ];

    return Promise.all(initialRequests)
      .then(([
        campaign,
        adGroup,
        adData,
      ]) => ({
        campaign,
        adGroup,
        adData,
        companyInfo: connectedCompanyPage,
      })).catch((error) => {
        logger.trace(`Failed to load ad data: ${JSON.stringify(error)}`);
        throw error;
      });
  }, [loadAdGroup, loadCampaign, loadAd, connectedCompanyPage, logger]);

  const saveCampaign = useCallback((campaign) => {
    const campaignToUpdate = {
      Id: campaign.Id,
      Name: campaign.Name,
    };

    const campaignToCreate = {
      ...campaign,
      Id: -1,
      AdGroups: [{
        Name: 'Ad Group 1',
        Campaign: { Id: -1 },
      }],
    };

    const campaignToSave = campaign.Id ? campaignToUpdate : campaignToCreate;

    return createOrUpdateCampaign(campaignToSave);
  }, []);

  const saveAds = useCallback((campaign, adGroup, adData) => {
    const modelToSave = mapLinkedInAdToBulkModel(campaign, adGroup, adData);

    return createOrUpdateAd(modelToSave);
  }, [mapLinkedInAdToBulkModel]);

  const save = useCallback(({ data }) => {
    const { campaign, adGroup, adData } = data;

    const saveCampaignPromise = saveCampaign(campaign, adGroup);

    return saveCampaignPromise
      .then((campaignRes) => {
        logger.trace(`Campaign updated successfully: ${JSON.stringify(campaignRes)}`);
        const { Id, AdGroups = [] } = campaignRes;
        const adGroupRes = adGroup && adGroup.Id ? adGroup : AdGroups[0];

        campaign.Id = Id;
        adGroup.Id = adGroupRes.Id;

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
          campaignId: Number(Id),
          adGroupId: Number(adGroupRes.Id),
        });

        return saveAds(campaign, adGroupRes, adData);
      })
      .then((adRes) => {
        logger.trace(`Ad updated successfully: ${JSON.stringify(adRes)}`);
        adData.Id = adRes.Id;

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
          editingAdId: adRes.Id,
          ads: {
            ...ads,
            [adRes.Id]: adData,
          },
        });

        return data;
      })
      .catch((error) => {
        logger.error(`Failed to save ad data: ${JSON.stringify(error)}`);
        const { errors } = error || {};
        if (!_.isEmpty(errors)) {
          const serverErrors
          = mapBulkUpsertErrors(errors, bulkUpsertEntityErrorMap, _.identity, null, i18n);

          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({ error: { serverErrors } });
        }
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ error: { serverErrors: [genericApiError] } });
      });
  }, [saveCampaign, send, saveAds, i18n, logger, ads]);

  return {
    load,
    save,
  };
};
